import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ForumTopicStatus } from '../model/forumTopicStatus';
import { GetForumTopicsResult } from '../model/getForumTopicsResult';
import { GetForumTopicResult } from '../model/getForumTopicResult';
import { CreateForumTopicCommand } from '../model/createForumTopicCommand';
import { CreateForumTopicResult } from '../model/createForumTopicResult';
import { GetForumCommentsResult } from '../model/getForumCommentsResult';
import { CreateForumCommentCommand } from '../model/createForumCommentCommand';
import { CreateForumCommentResult } from '../model/createForumCommentResult';
import { ToggleForumCommentReactionCommand } from '../model/toggleForumCommentReactionCommand';
import { ToggleForumCommentReactionResult } from '../model/toggleForumCommentReactionResult';
import { GetForumCommentReportsResult } from '../model/getForumCommentReportsResult';
import { ReportForumCommentCommand } from '../model/reportForumCommentCommand';
import { BanFromForumTopicCommand } from '../model/banFromForumTopicCommand';
import { GetForumBanRequestsResult } from '../model/getForumBanRequestsResult';
import { GetForumTopicBansResult } from '../model/getForumTopicBansResult';
import { RequestForumTopicBanCommand } from '../model/requestForumTopicBanCommand';
import { BASE_PATH } from '../variables';
import { Configuration } from '../configuration';

@Injectable()
export class ForumService {
  protected basePath = '/';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  constructor(
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration
  ) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }

  private authHeaders(): HttpHeaders {
    let headers = this.defaultHeaders;
    if (this.configuration.apiKeys?.['Authorization']) {
      headers = headers.set('Authorization', this.configuration.apiKeys['Authorization']);
    }
    return headers;
  }

  forumTopicsGet(
    status?: ForumTopicStatus,
    search?: string,
    mineOnly?: boolean,
    pageNumber?: number,
    pageSize?: number,
    language?: string,
    savedOnly?: boolean,
  ): Observable<GetForumTopicsResult> {
    let params = new HttpParams();
    if (status) params = params.set('Status', status);
    if (search) params = params.set('Search', search);
    if (mineOnly === true) params = params.set('MineOnly', 'true');
    if (savedOnly === true) params = params.set('SavedOnly', 'true');
    params = params.set('PageNumber', String(pageNumber ?? 1));
    params = params.set('PageSize', String(pageSize ?? 10));
    if (language) params = params.set('Language', language);

    return this.httpClient.get<GetForumTopicsResult>(`${this.basePath}/Forum/Topics`, {
      withCredentials: this.configuration.withCredentials,
      headers: this.authHeaders(),
      params,
    });
  }

  forumTopicsUidGet(uid: string): Observable<GetForumTopicResult> {
    return this.httpClient.get<GetForumTopicResult>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsPost(body: CreateForumTopicCommand): Observable<CreateForumTopicResult> {
    return this.httpClient.post<CreateForumTopicResult>(`${this.basePath}/Forum/Topics`, body, {
      withCredentials: this.configuration.withCredentials,
      headers: this.authHeaders(),
    });
  }

  forumTopicsPostForm(
    title: string,
    body: string,
    files: File[] = [],
    coverImage?: File | null,
  ): Observable<CreateForumTopicResult> {
    const formData = new FormData();
    formData.append('Title', title);
    formData.append('Body', body);
    if (coverImage) {
      formData.append('CoverImage', coverImage, coverImage.name);
    }
    for (const file of files) {
      formData.append('Files', file, file.name);
    }

    return this.httpClient.post<CreateForumTopicResult>(
      `${this.basePath}/Forum/Topics`,
      formData,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      },
    );
  }

  forumTopicsUidPutForm(
    uid: string,
    title: string,
    body: string,
    options: {
      files?: File[];
      coverImage?: File | null;
      removeCoverImage?: boolean;
      removeImageUids?: string[];
    } = {},
  ): Observable<{ uid?: string }> {
    const formData = new FormData();
    formData.append('Title', title);
    formData.append('Body', body);

    if (options.coverImage) {
      formData.append('CoverImage', options.coverImage, options.coverImage.name);
    }
    if (options.removeCoverImage) {
      formData.append('RemoveCoverImage', 'true');
    }
    for (const file of options.files ?? []) {
      formData.append('Files', file, file.name);
    }
    for (const imageUid of options.removeImageUids ?? []) {
      formData.append('RemoveImageUids', imageUid);
    }

    return this.httpClient.put<{ uid?: string }>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}`,
      formData,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      },
    );
  }

  forumTopicsUidSavePost(uid: string): Observable<{ topicUid?: string; savedByMe?: boolean }> {
    return this.httpClient.post<{ topicUid?: string; savedByMe?: boolean }>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/Save`,
      {},
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      },
    );
  }

  forumTopicsUidMutePost(uid: string): Observable<{ topicUid?: string; mutedByMe?: boolean }> {
    return this.httpClient.post<{ topicUid?: string; mutedByMe?: boolean }>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/Mute`,
      {},
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      },
    );
  }

  forumTopicsUidApprovePost(uid: string): Observable<void> {
    return this.httpClient.post<void>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/Approve`,
      {},
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidRejectPost(uid: string): Observable<void> {
    return this.httpClient.post<void>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/Reject`,
      {},
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidClosePost(uid: string): Observable<void> {
    return this.httpClient.post<void>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/Close`,
      {},
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidDelete(uid: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidCommentsGet(uid: string): Observable<GetForumCommentsResult> {
    return this.httpClient.get<GetForumCommentsResult>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/Comments`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidCommentsPost(
    uid: string,
    body: CreateForumCommentCommand
  ): Observable<CreateForumCommentResult> {
    return this.httpClient.post<CreateForumCommentResult>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/Comments`,
      body,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidCommentsPostForm(
    uid: string,
    commentBody: string,
    parentCommentUid?: string,
    files: File[] = [],
  ): Observable<CreateForumCommentResult> {
    const formData = new FormData();
    formData.append('Body', commentBody ?? '');
    if (parentCommentUid) {
      formData.append('ParentCommentUid', parentCommentUid);
    }
    for (const file of files) {
      const fileName = file.name?.trim() || `forum_${Date.now()}.jpg`;
      formData.append('Files', file, fileName);
    }

    return this.httpClient.post<CreateForumCommentResult>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/Comments`,
      formData,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      },
    );
  }

  forumCommentsCommentUidReactionsPost(
    commentUid: string,
    body: ToggleForumCommentReactionCommand
  ): Observable<ToggleForumCommentReactionResult> {
    return this.httpClient.post<ToggleForumCommentReactionResult>(
      `${this.basePath}/Forum/Comments/${encodeURIComponent(commentUid)}/Reactions`,
      body,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumCommentsCommentUidDelete(commentUid: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.basePath}/Forum/Comments/${encodeURIComponent(commentUid)}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumCommentsCommentUidPut(
    commentUid: string,
    body: { body: string }
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${this.basePath}/Forum/Comments/${encodeURIComponent(commentUid)}`,
      body,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumCommentsCommentUidReportPost(
    commentUid: string,
    body: ReportForumCommentCommand
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${this.basePath}/Forum/Comments/${encodeURIComponent(commentUid)}/Report`,
      body,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidBanOwnerUidPost(
    topicUid: string,
    ownerUid: string,
    body: BanFromForumTopicCommand = {},
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(topicUid)}/Ban/${encodeURIComponent(ownerUid)}`,
      body,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidBanRequestsPost(
    topicUid: string,
    body: RequestForumTopicBanCommand,
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(topicUid)}/BanRequests`,
      body,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidBanRequestsGet(uid: string): Observable<GetForumBanRequestsResult> {
    return this.httpClient.get<GetForumBanRequestsResult>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/BanRequests`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidBansGet(uid: string): Observable<GetForumTopicBansResult> {
    return this.httpClient.get<GetForumTopicBansResult>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/Bans`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidBanOwnerUidDelete(topicUid: string, ownerUid: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(topicUid)}/Ban/${encodeURIComponent(ownerUid)}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidBanRequestsRequestUidRejectPost(
    topicUid: string,
    requestUid: string,
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(topicUid)}/BanRequests/${encodeURIComponent(requestUid)}/Reject`,
      {},
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }

  forumTopicsUidCommentReportsGet(uid: string): Observable<GetForumCommentReportsResult> {
    return this.httpClient.get<GetForumCommentReportsResult>(
      `${this.basePath}/Forum/Topics/${encodeURIComponent(uid)}/CommentReports`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.authHeaders(),
      }
    );
  }
}
